import { config } from '@/config';
import { API_REQUEST_TIMEOUT_MS } from '@/lib/query-constants';

interface McpEnvelope<T> {
  success: boolean;
  data: T;
}

export interface McpAuthorizeRequestInfo {
  clientName: string;
  scope: string | null;
}

export interface McpAuthorizeResult {
  redirectUri: string;
  clientName?: string;
}

/**
 * These calls go to services/mcp, not services/api. They deliberately omit
 * `credentials: 'include'` — approving a chat client must not read or write
 * the Nexus web session, and MCP has no cookies of its own.
 */
async function mcpFetch<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${config.mcpUrl}${path}`, {
    ...init,
    headers: { 'Content-Type': 'application/json', ...init?.headers },
    signal: AbortSignal.timeout(API_REQUEST_TIMEOUT_MS),
  });

  if (!res.ok) {
    const body = (await res.json().catch(() => null)) as
      | { error?: { message?: string }; error_description?: string }
      | null;
    throw new Error(
      body?.error?.message ?? body?.error_description ?? `Request failed: ${res.status}`,
    );
  }

  const json = (await res.json()) as McpEnvelope<T>;
  return json.data;
}

/** Names the chat client on the consent screen so the user knows what they are approving. */
export function getMcpAuthorizeRequest(requestId: string) {
  return mcpFetch<McpAuthorizeRequestInfo>(
    `/authorize/request/${encodeURIComponent(requestId)}`,
  );
}

export function approveMcpAuthorize(requestId: string, firebaseToken: string) {
  return mcpFetch<McpAuthorizeResult>('/authorize/complete', {
    method: 'POST',
    body: JSON.stringify({ requestId, firebaseToken }),
  });
}

export function denyMcpAuthorize(requestId: string) {
  return mcpFetch<McpAuthorizeResult>('/authorize/deny', {
    method: 'POST',
    body: JSON.stringify({ requestId }),
  });
}
